import React, { useEffect } from "react";
import Head from "next/head";
import { FooterSite, HeaderSite, headerVariants } from "@acme/feature";
import { motion } from "framer-motion";

const TALLY_EMBED_URL = "https://tally.so/r/2Ea67g?embed=true"; // Replace with your Tally form URL

const CustomAiWorkflowPage = () => {
    useEffect(() => {
        document.documentElement.classList.remove('dark');
        document.body.classList.remove('dark');
    }, []);

    const tallyEmbedUrlFixed = TALLY_EMBED_URL;


    return (
        <>
            <Head>
                <title>Custom AI Workflow & App Builds - DapperGPT</title>
                <meta
                    name="description"
                    content="Need a custom AI workflow or lightweight app? We help teams and individuals design and build practical AI workflows. Contact us or submit the form to get started."
                />
                <meta property="og:title" content="Custom AI Workflow & App Builds" />
                <meta
                    property="og:description"
                    content="Tell us what you want to build. We’ll get back to you with questions, scope, and next steps."
                />
            </Head>

            <div className="bg-white dark:bg-gray-900 min-h-screen flex flex-col font-sans text-gray-900 dark:text-gray-100 selection:bg-blue-500/30 transition-colors duration-300">
                <HeaderSite containerClassName="!max-w-7xl px-6" />

                <main className="flex-grow">
                    <section className="relative pt-20 pb-14 overflow-hidden">
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-orange-100/50 dark:bg-orange-900/20 blur-[120px] rounded-full pointer-events-none" />

                        <div className="container mx-auto px-4 relative z-10">
                            <motion.div initial="hidden" animate="visible" variants={headerVariants} className="max-w-3xl mx-auto text-center">
                                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 text-orange-700 dark:bg-orange-900/30 dark:border-orange-800 dark:text-orange-300 text-sm font-medium mb-6">
                                    <span aria-hidden className="animate-pulse">🔥</span>
                                    Custom builds
                                </span>

                                <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-5 bg-clip-text text-transparent bg-gradient-to-b from-gray-900 to-gray-600 dark:from-white dark:to-gray-400 leading-tight">
                                    Build a custom AI workflow or app
                                </h1>

                                <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed">
                                    If you’re a company or an individual and you want to automate an internal process,
                                    ship a client-facing feature, or build an AI workflow end-to-end—reach out and we’ll help you plan and ship it.
                                </p>

                                <div className="mt-6 max-w-3xl mx-auto text-left">
                                    <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm p-5">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white">Common requests</p>
                                        <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                                            <li className="flex items-start gap-2"><span aria-hidden className="mt-0.5 text-orange-500">•</span><span>Support inbox triage + auto-drafting replies</span></li>
                                            <li className="flex items-start gap-2"><span aria-hidden className="mt-0.5 text-orange-500">•</span><span>Client reporting + automatic summaries</span></li>
                                            <li className="flex items-start gap-2"><span aria-hidden className="mt-0.5 text-orange-500">•</span><span>Document / PDF analysis workflows</span></li>
                                            <li className="flex items-start gap-2"><span aria-hidden className="mt-0.5 text-orange-500">•</span><span>Lead qualification + routing automation</span></li>
                                            <li className="flex items-start gap-2"><span aria-hidden className="mt-0.5 text-orange-500">•</span><span>Internal knowledge base Q&A</span></li>
                                            <li className="flex items-start gap-2"><span aria-hidden className="mt-0.5 text-orange-500">•</span><span>Custom chat (RAG) for your product</span></li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                                    <a
                                        href="mailto:support@dappergpt.com?subject=Custom%20AI%20Workflow%20-%20DapperGPT"
                                        className="px-6 py-3 bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
                                    >
                                        Contact us
                                    </a>
                                    {/*  <Link
                                        href="/chat"
                                        className="px-6 py-3 bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700 rounded-lg font-semibold transition-all"
                                    >
                                        Try us
                                    </Link> */}
                                </div>

                                <p className="mt-6 text-sm text-gray-500 dark:text-gray-500">
                                    Prefer forms? Submit the details below and we’ll get back to you.
                                </p>
                            </motion.div>
                        </div>
                    </section>

                    <section className="pb-24">
                        <div className="container mx-auto px-4">
                            <div className="max-w-4xl mx-auto">
                                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm overflow-hidden">
                                    <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800">
                                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Project intake</h2>
                                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                            Tell us what you’re building, who it’s for (internal or client-facing), your timeline, and what “done” looks like.
                                        </p>
                                    </div>

                                    <div className="p-3 sm:p-5">
                                        {tallyEmbedUrlFixed ? (
                                            <iframe
                                                title="Custom AI Workflow - Tally Form"
                                                src={tallyEmbedUrlFixed}
                                                className="w-full h-[820px]"
                                                loading="lazy"
                                                referrerPolicy="no-referrer"
                                            />
                                        ) : (
                                            <div className="px-6 py-10 text-center">
                                                <div className="mt-5">
                                                    <a
                                                        href="mailto:support@dappergpt.com?subject=Custom%20AI%20Workflow%20-%20DapperGPT"
                                                        className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-gray-900 text-white hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 font-semibold transition-colors"
                                                    >
                                                        Email us instead
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {["AI workflow design", "Custom app build", "Automation & integrations"].map((label) => (
                                        <div
                                            key={label}
                                            className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/40 backdrop-blur-sm p-5"
                                        >
                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
                                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                                Practical scope, fast iterations, and clean handoff.
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </main>

                <FooterSite />
            </div>
        </>
    );
};

export default CustomAiWorkflowPage;
